import { useContext, useMemo } from "react";
import HeadsetMicRoundedIcon from "@mui/icons-material/HeadsetMicRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import CardGiftcardRoundedIcon from "@mui/icons-material/CardGiftcardRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import EventSeatRoundedIcon from "@mui/icons-material/EventSeatRounded";
import RoomServiceRoundedIcon from "@mui/icons-material/RoomServiceRounded";
import UserContext from "../../context/User";
import useBlackCardProgram from "../../hooks/useBlackCardProgram";

/**
 * Single source of truth for the customer navigation list. Both the desktop
 * SideNav and the mobile drawer pull from here so the two stay in sync.
 */
const NAV_ITEMS = [
  { label: "Home", icon: HomeRoundedIcon, path: "/" },
  // /pickup is the same list in pickup mode, so Restaurants stays lit there.
  { label: "Restaurants", icon: StorefrontRoundedIcon, path: "/restaurant-list", alsoOn: ["/pickup"] },
  { label: "Catering", icon: RoomServiceRoundedIcon, path: "/catering" },
  { label: "Deals", icon: LocalOfferRoundedIcon, path: "/deals" },
  { label: "Favourites", icon: FavoriteRoundedIcon, path: "/favourite", requiresAuth: true },
  { label: "My Orders", icon: ReceiptLongRoundedIcon, path: "/orders", requiresAuth: true },
  { label: "Reservations", icon: EventSeatRoundedIcon, path: "/reservations", requiresAuth: true },
  { label: "Support Tickets", icon: SupportAgentRoundedIcon, path: "/my-tickets", requiresAuth: true },
  { label: "Wallet", icon: AccountBalanceWalletRoundedIcon, path: "/wallet", requiresAuth: true },
  {
    label: "My Rewards", icon: StarRoundedIcon, path: "/rewards", requiresAuth: true,
    children: [
      { label: "My Coupons", icon: CardGiftcardRoundedIcon, path: "/my-coupons" },
      { label: "Promotional Coupons", icon: LocalOfferRoundedIcon, path: "/promotional-coupons" },
      { label: "Black Card", icon: CardGiftcardRoundedIcon, path: "/membership", blackCard: true },
    ],
  },
  { label: "Settings", icon: SettingsRoundedIcon, path: "/settings", requiresAuth: true },
  { label: "Get Help", icon: HeadsetMicRoundedIcon, path: "/get-help", getHelp: true },
];

/**
 * NAV_ITEMS as this customer should see them. While the super admin has Black
 * Card turned off, its entry shows only to members, who still need it to
 * manage their membership.
 */
export function useNavItems() {
  const { profile } = useContext(UserContext);
  const { program, loaded } = useBlackCardProgram();
  const showBlackCard = !!profile?.hasBlackCard || (loaded && program.enabled);
  return useMemo(() => {
    if (showBlackCard) return NAV_ITEMS;
    return NAV_ITEMS.map((item) => (item.children
      ? { ...item, children: item.children.filter((c) => !c.blackCard) }
      : item));
  }, [showBlackCard]);
}

export default NAV_ITEMS;
